import { createContext, useContext, useState, useEffect } from "react";
import { MealTagApi } from "../api/MealTagApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const MealTagContext = createContext();

export const useMealTags = () => {
  return useContext(MealTagContext);
};

export const MealTagProvider = ({ children }) => {
  const [mealTags, setMealTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMealTags = async () => {
    try {
      setLoading(true);
      const response = await MealTagApi.fetchAll();
      setMealTags(response);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to fetch meal tags", error, { correlationId });
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const addMealTag = async (tagContent) => {
    try {
      const created = await MealTagApi.create({ tagContent });
      setMealTags((prev) => [...prev, created]);
      toast.success("Meal tag created");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to create meal tag", error, {
        correlationId,
        tagContent,
      });
      toast.error(getErrorMessage(error));
    }
  };

  const removeMealTag = async (tagId) => {
    try {
      await MealTagApi.deleteById(tagId);
      setMealTags((prev) => prev.filter((tag) => tag.tagId !== tagId));
      toast.success("Meal tag deleted");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to delete meal tag", error, {
        correlationId,
        tagId,
      });
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchMealTags();
  }, []);

  const value = {
    mealTags,
    loading,
    addMealTag,
    removeMealTag,
  };

  return (
    <MealTagContext.Provider value={value}>
      {children}
    </MealTagContext.Provider>
  );
};
